import * as React from 'react';

import { cn } from '~/utils/cn';

export interface SwitchProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange'
> {
  checked?: boolean | undefined;
  onCheckedChange?: ((checked: boolean) => void) | undefined;
  /** `sm` is a dense-table variant (e.g. a per-row toggle in a settings table) — same colours and
   *  focus ring, a smaller track. Default is unchanged for every existing caller. */
  size?: 'default' | 'sm' | undefined;
  ref?: React.Ref<HTMLButtonElement> | undefined;
}

const TRACK_SIZE_CLASS = { default: 'h-6 w-11', sm: 'h-[18px] w-8' } as const;
const THUMB_SIZE_CLASS = { default: 'h-4 w-4', sm: 'h-3.5 w-3.5' } as const;
const THUMB_TRANSLATE_CLASS = { default: 'translate-x-6', sm: 'translate-x-3.5' } as const;

function Switch({
  className,
  checked = false,
  onCheckedChange,
  size = 'default',
  ref,
  ...props
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      ref={ref}
      className={cn(
        // `relative` + the `before:` pseudo expand the touch target to 44×44px
        // (WCAG 2.5.5 minimum) without changing the visual size of the track.
        // The pseudo is decorative — invisible to AT — and only enlarges the
        // hit area, so small-screen taps and motor-impaired users hit the
        // control reliably.
        'focus-visible:ring-ring focus-visible:ring-offset-background peer relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors before:absolute before:-inset-x-0 before:-inset-y-2.5 before:content-[""] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
        TRACK_SIZE_CLASS[size],
        checked ? 'bg-primary' : 'bg-input',
        className,
      )}
      onClick={() => {
        onCheckedChange?.(!checked);
      }}
      {...props}
    >
      <span
        className={cn(
          'bg-background pointer-events-none block rounded-full shadow-lg ring-0 transition-transform',
          THUMB_SIZE_CLASS[size],
          checked ? THUMB_TRANSLATE_CLASS[size] : 'translate-x-0',
        )}
      />
    </button>
  );
}

export { Switch };
